import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "./Reveal";

const PHONE_IMG =
  "https://images.unsplash.com/photo-1700864208310-0849428c612f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxjbGVhbiUyMGFlc3RoZXRpYyUyMHBob25lJTIwbW9ja3VwJTIwaW4lMjBoYW5kfGVufDB8fHx8MTc4NDEzMjkxMXww&ixlib=rb-4.1.0&q=85";
const MOOD_IMG =
  "https://images.unsplash.com/photo-1620915789294-c972b1b1af7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxlZGl0b3JpYWwlMjB3ZWxsbmVzcyUyMGFlc3RoZXRpY3N8ZW58MHx8fHwxNzg0MTMyOTExfDA&ixlib=rb-4.1.0&q=85";

const moments = [
  { time: "Morning", text: "You wake to a warm hello and a single, gentle intention for the day." },
  { time: "Midday", text: "A quiet health tip and a tidy list keep you steady without the noise." },
  { time: "Evening", text: "Reminders soften, the day is closed kindly, and tomorrow waits patiently." },
];

export const DayNarrative = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);

  return (
    <section id="day" ref={ref} className="py-24 md:py-40 bg-[#EDE8DF]/50" data-testid="day-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20 items-start">
        {/* Sticky text */}
        <div className="md:sticky md:top-28">
          <Reveal>
            <p className="font-body text-xs sm:text-sm uppercase tracking-[0.25em] text-[#2B2B26]/60 mb-6">
              A day with My Life
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-[#2B2B26] mb-10">
              Gentle from the first light to the last.
            </h2>
          </Reveal>
          <div className="space-y-8">
            {moments.map((m, i) => (
              <Reveal key={m.time} delay={0.15 + i * 0.1}>
                <div className="flex gap-6 border-t border-[#2B2B26]/15 pt-5">
                  <span className="font-body text-xs uppercase tracking-[0.2em] text-[#1877F2] w-20 shrink-0 pt-1">
                    {m.time}
                  </span>
                  <p className="font-body text-lg leading-relaxed text-[#2B2B26]/75 font-light">
                    {m.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Parallax imagery */}
        <div className="relative grid grid-cols-2 gap-5 md:gap-6">
          <motion.div style={{ y: y1 }} className="col-span-2">
            <div className="overflow-hidden rounded-[2rem] border border-[#2B2B26]/5 shadow-[0_20px_40px_rgb(43,43,38,0.08)]">
              <img
                src={MOOD_IMG}
                alt="Calm wellness lifestyle moment"
                className="w-full h-[38vh] md:h-[46vh] object-cover"
              />
            </div>
          </motion.div>
          <motion.div style={{ y: y2 }} className="col-span-1">
            <div className="overflow-hidden rounded-[2rem] border border-[#2B2B26]/5 shadow-[0_20px_40px_rgb(43,43,38,0.08)]">
              <img
                src={PHONE_IMG}
                alt="My Life app in hand"
                className="w-full h-[30vh] md:h-[38vh] object-cover"
              />
            </div>
          </motion.div>
          <motion.div style={{ y: y2 }} className="col-span-1 flex">
            <div className="w-full rounded-[2rem] bg-[#2B2B26] text-[#F8F7F1] p-7 flex flex-col justify-between">
              <span className="font-body text-[10px] uppercase tracking-[0.2em] text-[#F8F7F1]/60">
                Today's tip
              </span>
              <p className="font-serif text-xl md:text-2xl leading-snug mt-6">
                “Sip one full glass of water before you wind down — sleep comes softer.”
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DayNarrative;
